import { cursorGlow } from '@schmancy/directives/cursor-glow'
import { componentText, HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import '@schmancy/primitives/button'
import '@schmancy/primitives/file-item'
import '@schmancy/primitives/icons'
import '@schmancy/primitives/list'
import '@schmancy/primitives/typography'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { map, Subject, takeUntil, tap } from 'rxjs'
import { acceptRefusal, acceptsFile, type FileRefusal } from './accept'

export type SchmancyDropzoneChangeEvent = CustomEvent<{ value: File[] }>
/** The files this intake just accepted — the delta, not the whole held set. */
export type SchmancyDropzoneFileEvent = CustomEvent<{ files: File[] }>
/** Fires on the transitions only, never once per `dragover` frame. */
export type SchmancyDropzoneDragEvent = CustomEvent<{ active: boolean }>

/**
 * @element schmancy-dropzone
 *
 * File intake: a drop target, the native picker behind it, paste, accepted-type
 * validation, and a readout of what was chosen. Its value is the `File`s
 * themselves.
 *
 * WHY THIS IS NOT `<schmancy-attachments>` IN ANOTHER SKIN, and why neither is
 * built on the other:
 *
 * - `<schmancy-attachments>` re-encodes every file to a `data:` URI it then
 *   holds as its value, because its consumers write that URI into a document
 *   with a hard size limit. A surface that promises the buyer their file never
 *   leaves the device cannot hold an encoded copy of it at all — this control
 *   holds only the `File` handle the platform already gave the page, the same
 *   handle a bare `<input type="file">` holds, and hands it on.
 * - `accept` here admits anything the platform's own `accept` grammar admits,
 *   PDFs included. Attachments refuses whatever its canvas encoder cannot
 *   decode, which is every non-image, so a printed-to-PDF ticket sheet has no
 *   way through it.
 * - The two also count differently: attachments counts encoded results,
 *   this counts handles, so one cannot drive the other's ceiling.
 *
 * The one thing they genuinely shared — what `accept` admits, and what a
 * refusal says — is `./accept`, which both import, so a file refused by one is
 * refused by the other in the same words.
 *
 * @slot - The prompt copy inside the target. Defaults to a sentence naming all
 *   three ways in.
 * @slot preview - Replaces the default file readout with consumer-owned previews.
 * @fires change - `{ value: File[] }` — the whole held set, after any change.
 * @fires file - `{ files: File[] }` — only the files just accepted.
 * @fires refusal - `{ file, cause, message }` — one per file turned away, so an
 *   app can answer a refusal by its cause instead of only reading the one
 *   sentence this control paints for everyone. Shared with
 *   `<schmancy-attachments>` through `./accept`.
 * @fires dragover - `{ active: boolean }` — a semantic transition, not the
 *   native per-frame `DragEvent`, which this element consumes.
 */
@customElement('schmancy-dropzone')
export class SchmancyDropzone extends HostAriaLabelMixin(SchmancyFormField(css`
	:host {
		display: block;
	}
`)) {
	/**
	 * Mirrors the native attribute, and is applied to dropped and pasted files
	 * too — where the platform applies nothing at all. Empty accepts everything.
	 */
	@property({ type: String }) accept = ''

	/** A compact portrait target alongside previews; the default fills its host. */
	@property({ type: String }) variant: 'default' | 'tile' = 'default'

	/** A pointer-following glow on the tile target only. */
	@property({ type: Boolean }) glow = false

	/** How many files the control will hold. Above one, the picker goes multiple. */
	@property({ type: Number }) max = 1

	/** Per-file byte ceiling, checked against `File.size`. The file is never read. */
	@property({ type: Number, attribute: 'max-bytes' }) maxBytes = Number.POSITIVE_INFINITY

	/**
	 * The array instance the caller passes in is stored as-is: the mixin tracks
	 * `dirty` by comparing `value` against the default it captured by reference,
	 * and every internal edit replaces the array wholesale, so preserving the
	 * caller's instance is what lets `form.reset()` land back on pristine
	 * instead of leaving a required field painting its error the moment it is
	 * reset. Not reflected — an array in an attribute stringifies to junk.
	 */
	@property({ attribute: false })
	override get value(): File[] {
		return this.files
	}
	override set value(next: File[]) {
		this.files = Array.isArray(next) ? next : []
	}

	@state() private files: File[] = []
	@state() private dragging = false
	/** A transient refusal — a wrong type, an oversized file, one too many. Distinct from `validationMessage`, which is this field's own validity. */
	@state() private notice = ''

	private readonly fileInput = createRef<HTMLInputElement>()
	private readonly picked$ = new Subject<readonly File[]>()

	override connectedCallback(): void {
		super.connectedCallback()

		this.picked$
			.pipe(
				map(picked => {
					const accepted: File[] = []
					const refusals: FileRefusal[] = []
					for (const file of picked) {
						if (this.files.length + accepted.length >= this.max) {
							// The ceiling is one fact about every file still in hand, so it is
							// stated once, against the first file that met it.
							refusals.push({
								file,
								cause: 'tooMany',
								message:
									this.max === 1
										? componentText('dropzone.refusal.tooMany.one')
										: componentText('dropzone.refusal.tooMany.other', { max: this.max }),
							})
							break
						}
						if (!acceptsFile(this.accept, file)) {
							refusals.push({ file, cause: 'type', message: acceptRefusal(this.accept, file) })
							continue
						}
						if (file.size > this.maxBytes) {
							refusals.push({
								file,
								cause: 'tooLarge',
								message: componentText('dropzone.refusal.tooLarge', {
									name: file.name,
									size: Math.round(file.size / 1000),
									limit: Math.round(this.maxBytes / 1000),
								}),
							})
							continue
						}
						accepted.push(file)
					}
					return { accepted, refusals }
				}),
				tap(({ accepted, refusals }) => {
					// The notice is the last refusal's own words rather than a second
					// sentence written beside it, so the one on screen and the one on the
					// event can never disagree.
					this.notice = refusals.at(-1)?.message ?? ''
					for (const refusal of refusals) this.dispatchScopedEvent('refusal', refusal, { bubbles: true })
					if (accepted.length === 0) return
					this.value = [...this.files, ...accepted]
					this.markTouched()
					this.dispatchScopedEvent('file', { files: accepted }, { bubbles: true })
					this.emitChange({ value: this.files })
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/** Drops every held file, as the remove control does. Emits `change`. */
	public clear(): void {
		if (this.files.length === 0) return
		this.notice = ''
		this.value = []
		this.markTouched()
		this.emitChange({ value: this.files })
	}

	/**
	 * The platform's `setFormValue` takes no arrays, so the submitted value is a
	 * FormData carrying one `File` per entry under the field's name — the shape
	 * a native multiple file input submits, and the shape `toFormEntries()`
	 * contributes. The same FormData is the restore state: `File`s survive it,
	 * so a bfcache restore comes back with the real handles rather than names.
	 */
	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (!changed.has('files') && !changed.has('value') && !changed.has('name')) return

		const submitted = new FormData()
		if (this.name) for (const file of this.files) submitted.append(this.name, file)
		this.internals?.setFormValue(submitted, submitted)
	}

	/** The mixin's required test is `value === ''`; an empty list is not `''`, so a required field with nothing chosen would otherwise pass. */
	override checkValidity(): boolean {
		if (this.required && !this.disabled && this.files.length === 0) {
			return this.setFieldValidity({ valueMissing: true }, this.errorMessages?.valueMissing ?? 'This field is required')
		}
		return super.checkValidity()
	}

	/** One entry per file, each carrying the `File` itself. */
	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled) return []
		return this.files.map(file => [this.name, file] as [string, FormDataEntryValue])
	}

	/** The mixin assigns the restore state straight to `value`; this field's is a FormData of files. */
	override formStateRestoreCallback(restoreState: string | File | FormData | null): void {
		if (!(restoreState instanceof FormData)) return
		this.value = restoreState.getAll(this.name).filter(entry => entry instanceof File)
	}

	override render() {
		const full = this.files.length >= this.max
		const locked = this.disabled || this.readonly

		return html`
			<!-- min-h-full + grow on the target, so a host given a definite height
			     (a drop target shaped like the thing being dropped into it) has
			     that height reach the target rather than stopping at the wrapper.
			     Against an auto-height host the percentage resolves to nothing and
			     the target keeps its px-6 py-6 measure. -->
			<div
				class=${this.classMap({
					'flex gap-2': true,
					'min-h-full flex-col': this.variant === 'default',
					'flex-wrap items-start': this.variant === 'tile',
				})}
				@dragover=${(e: DragEvent) => {
					// The native event is consumed here rather than allowed out:
					// this element IS the drop target, and letting a DragEvent
					// named `dragover` escape alongside the semantic CustomEvent of
					// the same name would leave a consumer's one handler receiving
					// two unrelated event shapes.
					e.stopPropagation()
					if (locked || full) return
					e.preventDefault()
					if (this.dragging) return
					this.dragging = true
					this.dispatchScopedEvent('dragover', { active: true }, { bubbles: true })
				}}
				@dragleave=${(e: DragEvent) => {
					const leftFor = e.relatedTarget
					if (leftFor instanceof Node && (e.currentTarget as Node).contains(leftFor)) return
					if (!this.dragging) return
					this.dragging = false
					this.dispatchScopedEvent('dragover', { active: false }, { bubbles: true })
				}}
				@drop=${(e: DragEvent) => {
					if (this.dragging) {
						this.dragging = false
						this.dispatchScopedEvent('dragover', { active: false }, { bubbles: true })
					}
					if (locked) return
					const dropped = Array.from(e.dataTransfer?.files ?? [])
					if (dropped.length === 0) return
					e.preventDefault()
					this.picked$.next(dropped)
				}}
				@paste=${(e: ClipboardEvent) => {
					if (locked) return
					const pasted = Array.from(e.clipboardData?.items ?? [])
						.filter(item => item.kind === 'file')
						.map(item => item.getAsFile())
						.filter(file => file !== null)
					if (pasted.length === 0) return
					e.preventDefault()
					this.picked$.next(pasted)
				}}
			>
				${when(
					!!this.label,
					() => html`<schmancy-typography class="w-full" type="label" token="sm">${this.label}</schmancy-typography>`,
				)}
				<slot name="preview" class="contents">
					${when(
						this.files.length > 0,
						() => html`
							<schmancy-list>
								${repeat(
									this.files,
									file => `${file.name}:${file.size}:${file.lastModified}`,
									file => html`
										<schmancy-file-item
											readonly
											.name=${file.name}
											.kind=${file.type}
											.summary=${file.size < 1000
												? `${file.size} B`
												: file.size < 1_000_000
													? `${Math.round(file.size / 1000)} kB`
													: `${(file.size / 1_000_000).toFixed(1)} MB`}
										>
											${when(
												!locked,
												() => html`
													<schmancy-icon-button
														slot="trailing"
														size="xs"
														icon="close"
														aria-label="Remove ${file.name}"
														@click=${() => {
															this.notice = ''
															this.value = this.files.filter(held => held !== file)
															this.markTouched()
															this.emitChange({ value: this.files })
														}}
													></schmancy-icon-button>
												`,
											)}
										</schmancy-file-item>
									`,
								)}
							</schmancy-list>
						`,
					)}
				</slot>

				<!-- Unlabelled, the prompt copy is the target's own accessible name:
				     a fixed string would override whatever a consumer slotted and
				     give every unlabelled intake on a page the one name. The
				     description goes with it, since it pointed at the very element
				     now supplying the name — one sentence announced twice. -->
				<button
					${this.variant === 'tile' && this.glow
						? cursorGlow({ radius: 80, intensity: 0.16, color: 'var(--schmancy-sys-color-primary-default)' })
						: ''}
					type="button"
					aria-label=${ifDefined(this.ariaLabel || this.label || undefined)}
					aria-describedby=${ifDefined(this.ariaLabel || this.label ? 'dropzone-prompt' : undefined)}
					?disabled=${locked || full}
					?hidden=${this.variant === 'tile' && full}
					class=${this.classMap({
						'content-center justify-items-center text-center transition-colors': true,
						grid: this.variant !== 'tile' || !full,
						hidden: this.variant === 'tile' && full,
						'w-full grow gap-1 rounded-xl border-2 border-dashed px-6 py-6': this.variant === 'default',
						'h-23 w-18 shrink-0 rounded-2xl border border-dashed px-2': this.variant === 'tile',
						'border-control-border bg-surface-container': !this.dragging && !this.error,
						'border-primary-default bg-primary-container': this.dragging,
						'border-error-default': this.error && !this.dragging,
						'outline-2 outline-error-default': this.error,
						'hover:border-primary-default focus-visible:outline-2 focus-visible:outline-primary-default':
							!locked && !full,
						'opacity-40': locked || full,
					})}
					@click=${() => this.fileInput.value?.click()}
				>
					${when(
						this.variant === 'default',
						() => html`<schmancy-icon class="text-surface-onVariant" aria-hidden="true">upload_file</schmancy-icon>`,
					)}
					<schmancy-typography
						id="dropzone-prompt"
						type="body"
						token=${this.variant === 'tile' ? 'xs' : 'sm'}
						class="text-surface-onVariant"
					>
						${this.variant === 'tile'
							? html`<slot>${componentText('dropzone.add')}</slot>`
							: html`<slot>Drop a file, paste it, or click to choose one.</slot>`}
					</schmancy-typography>
				</button>

				${when(
					!!this.notice,
					() =>
						html`<schmancy-typography role="alert" type="body" token="xs" class="w-full text-error-default"
							>${this.notice}</schmancy-typography
						>`,
				)}
				${when(
					this.error && !!this.validationMessage,
					() =>
						html`<schmancy-typography role="alert" type="body" token="xs" class="w-full wrap-anywhere text-error-default"
							>${this.validationMessage}</schmancy-typography
						>`,
				)}
				${when(
					!!this.hint && !this.error,
					() =>
						html`<schmancy-typography type="body" token="xs" class="w-full wrap-anywhere text-surface-onVariant"
							>${this.hint}</schmancy-typography
						>`,
				)}

				<input
					${ref(this.fileInput)}
					type="file"
					class="sr-only"
					?hidden=${this.variant === 'tile'}
					tabindex="-1"
					aria-hidden="true"
					accept=${this.accept}
					?multiple=${this.max > 1}
					@change=${(e: Event) => {
						const input = e.target as HTMLInputElement
						const chosen = Array.from(input.files ?? [])
						input.value = ''
						if (chosen.length > 0) this.picked$.next(chosen)
					}}
				/>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-dropzone': SchmancyDropzone
	}
}
